import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import axiosService from '../../../../../utils/AxiosService'
import Input from '../../../../FormComponents/Input'
import ReactSelect from '../../../../FormComponents/ReactSelect'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import Modal from '../../../../Modal/Modal'
import { findExactTag, findSimilarTags } from '../../../../../utils/tagSimilarity'

export default observer(function TagModal({ isOpen, setIsOpen, authentication, onTagAdded = () => {} }) {
  const [allTags, setAllTags] = useState([])

  const assignedIds = useMemo(() => authentication.tags.map(tag => tag.id), [])
  const globalTags = useMemo(() => allTags.filter(tag => tag.campaign_id === null), [allTags])
  const tagOptions = useMemo(
    () => allTags.filter(tag => !assignedIds.includes(tag.id)).map(tag => ({ label: tag.name, value: tag.id })),
    [allTags]
  )

  useEffect(() => {
    axiosService.get('/api/project_manager/tags')
      .then(tags => setAllTags(tags))
      .catch(({ data: { error } }) => toastError(error))
  }, [])

  const handleSubmit = ({ tag_id, tag_name }, { setSubmitting }) => {
    const existing = !tag_id && findExactTag(tag_name, globalTags)
    const tagId = tag_id || existing?.id
    const request = tagId
      ? axiosService.post(`/api/project_manager/authentications/${authentication.id}/tags/${tagId}/add_to_authentication`)
      : axiosService.post(`/api/project_manager/authentications/${authentication.id}/tags/`, { tag: { name: tag_name.trim() } })

    return request
      .then(tag => {
        toastSuccess('Tag Added')
        setIsOpen(false)
        if (!authentication.tags.some(assigned => assigned.id === tag.id)) authentication.tags.push(tag)
        onTagAdded(tag)
      })
      .catch(({ data: { error } }) => {
        toastError(error)
        setSubmitting(false)
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <Modal.Header>
        <span className="font-bold text-lg dark:text-white">Add tag</span>
      </Modal.Header>
      <Formik
        initialValues={{ tag_name: '', tag_id: null, confirm_new: false }}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleBlur, values, setFieldValue, isSubmitting }) => {
          const exactMatch = values.tag_name.trim() ? findExactTag(values.tag_name, globalTags) : null
          const alreadyAssigned = exactMatch && assignedIds.includes(exactMatch.id)
          const similar = !exactMatch && values.tag_name.trim()
            ? findSimilarTags(values.tag_name, globalTags.filter(tag => !assignedIds.includes(tag.id)))
            : []
          const needsConfirmation = similar.length > 0 && !values.confirm_new
          const canSubmit = !isSubmitting && (values.tag_id || (values.tag_name.trim() && !alreadyAssigned && !needsConfirmation))

          const pickExisting = tag => {
            setFieldValue('tag_name', '')
            setFieldValue('confirm_new', false)
            setFieldValue('tag_id', tag.id)
          }

          return (
            <Form>
              <Modal.Body>
                <div className="w-[440px] flex flex-col">
                  {!values.tag_name && (
                    <ReactSelect
                      label="Choose an existing tag"
                      name="tag_id"
                      onChange={tag => setFieldValue('tag_id', tag?.value ?? null, true)}
                      onBlur={handleBlur}
                      value={values.tag_id}
                      options={tagOptions}
                      isClearable={true}
                    />
                  )}

                  {!values.tag_id && !values.tag_name && (
                    <div className="flex items-center gap-3 my-4">
                      <div className="h-px flex-1 bg-rb-lite-grey dark:bg-rb-dark-mode-lighter" />
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">or</span>
                      <div className="h-px flex-1 bg-rb-lite-grey dark:bg-rb-dark-mode-lighter" />
                    </div>
                  )}

                  {!values.tag_id && (
                    <Input
                      topMargin="mt-0"
                      inputPaddingClass="px-3"
                      label="Create a new tag"
                      name="tag_name"
                      onChange={event => {
                        handleChange(event)
                        setFieldValue('confirm_new', false)
                      }}
                      onBlur={handleBlur}
                      value={values.tag_name}
                    />
                  )}

                  {exactMatch && (
                    <p className="text-sm mt-2 text-gray-600 dark:text-gray-300">
                      {alreadyAssigned
                        ? <>This creator already has the tag <span className="font-semibold">{exactMatch.name}</span>.</>
                        : <>The tag <span className="font-semibold">{exactMatch.name}</span> already exists and will be added instead.</>}
                    </p>
                  )}

                  {similar.length > 0 && (
                    <div className="mt-3 rounded-lg border-1 border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40 p-3">
                      <p className="text-sm text-amber-800 dark:text-amber-300">Similar tags already exist. Use one of them to keep tags consistent:</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {similar.map(tag => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => pickExisting(tag)}
                            className="rounded-full px-3 py-1 text-xs font-semibold bg-blue-600/15 text-blue-700 hover:bg-blue-600/25 dark:text-blue-300"
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                      <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={values.confirm_new} onChange={event => setFieldValue('confirm_new', event.target.checked)} />
                        Create "{values.tag_name.trim()}" anyway
                      </label>
                    </div>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className="w-full flex justify-between items-center">
                  <Modal.DissmissButton className="rb-btn-cancel px-4 py-2 text-sm font-semibold rounded-lg">
                    Cancel
                  </Modal.DissmissButton>
                  <button
                    disabled={!canSubmit}
                    type="submit"
                    className="px-4 py-2 text-sm font-semibold text-white bg-rb-green rounded-lg dark:text-rb-dark-mode disabled:opacity-50"
                  >
                    {isSubmitting ? 'Adding...' : 'Add tag'}
                  </button>
                </div>
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
})
